<template>
  <b-card
    no-body
    class="report-table"
  >
    <b-card-body>
      <div class="d-flex justify-content-between align-items-start mb-1">
        <div>
          <h4 class="mb-25">
            {{ title }}
          </h4>
          <div
            v-if="subtitle"
            class="report-table__subtitle"
          >
            {{ subtitle }}
          </div>
        </div>

        <b-button
          v-if="rows.length"
          size="sm"
          variant="flat-secondary"
          title="خروجی اکسل (CSV)"
          @click="download"
        >
          <feather-icon
            icon="DownloadIcon"
            size="14"
          />
        </b-button>
      </div>

      <div
        class="report-table__body"
        :class="{ 'report-table__body--busy': loading }"
      >
        <b-table
          v-if="rows.length"
          :items="items"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          small
          hover
          responsive
          class="mb-0"
        >
          <!-- عنوان ردیف: تصویر + نام + زیرنویس -->
          <template #cell(__title)="data">
            <div class="d-flex align-items-center">
              <b-avatar
                v-if="showImage"
                :src="imageUrl(data.item.__image)"
                :text="initials(data.item.__title)"
                size="28"
                rounded
                class="mr-75 flex-shrink-0"
              />
              <div class="report-table__label">
                <component
                  :is="data.item.__route ? 'router-link' : 'span'"
                  v-bind="data.item.__route ? { to: data.item.__route } : {}"
                  class="report-table__name"
                >
                  {{ data.item.__title }}
                </component>
                <span
                  v-if="data.item.__subtitle"
                  class="report-table__sub"
                >{{ data.item.__subtitle }}</span>
              </div>
            </div>
          </template>

          <!-- سهم: نوار افقی نازک + عدد -->
          <template #cell(__share)="data">
            <div class="d-flex align-items-center">
              <div class="report-table__bar">
                <div
                  class="report-table__bar-fill"
                  :style="{ width: `${Math.min(data.item.__shareRaw, 100)}%` }"
                />
              </div>
              <span class="report-table__share-value">{{ data.item.__share }}</span>
            </div>
          </template>

          <!-- سلول متریک: مقدار + دلتا در صورت وجود مقایسه -->
          <template
            v-for="metric in metrics"
            #[`cell(${metric.Key})`]="data"
          >
            <div :key="metric.Key">
              <span class="report-table__value">{{ data.item[metric.Key] }}</span>
              <span
                v-if="data.item[`${metric.Key}__delta`]"
                :class="`report-table__delta text-${data.item[`${metric.Key}__variant`]}`"
              >
                {{ data.item[`${metric.Key}__delta`] }}
              </span>
            </div>
          </template>
        </b-table>

        <div
          v-else
          class="report-table__empty"
        >
          <feather-icon
            icon="InboxIcon"
            size="26"
          />
          <p class="mt-1 mb-0">
            {{ emptyText }}
          </p>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCardBody } from 'bootstrap-vue'

export default {
  name: 'ReportTable',
  components: { BCardBody },
  props: {
    // خروجی ReportModel.Breakdown از بک‌اند
    breakdown: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: null,
    },
    labelTitle: {
      type: String,
      default: 'عنوان',
    },
    showImage: {
      type: Boolean,
      default: false,
    },
    showShare: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'داده‌ای برای این بازه وجود ندارد',
    },
    /** تابعی که برای هر ردیف مقصد لینک را می‌سازد (اختیاری) */
    routeFor: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      sortBy: null,
      sortDesc: true,
    }
  },
  computed: {
    metrics() {
      return (this.breakdown && this.breakdown.Metrics) || []
    },

    rows() {
      return (this.breakdown && this.breakdown.Rows) || []
    },

    fields() {
      const list = [{ key: '__title', label: this.labelTitle, thClass: 'text-nowrap' }]

      this.metrics.forEach(metric => {
        list.push({
          key: metric.Key,
          label: metric.Title,
          sortable: true,
          thClass: 'text-nowrap',
          // مرتب‌سازی باید روی عدد خام انجام شود، نه روی رشته‌ی فرمت‌شده
          sortByFormatted: false,
        })
      })

      if (this.showShare && this.rows.some(r => r.Share !== null && r.Share !== undefined)) {
        list.push({ key: '__share', label: 'سهم', sortable: true, thClass: 'text-nowrap' })
      }

      return list
    },

    items() {
      return this.rows.map(row => {
        const item = {
          __title: row.Title || '—',
          __subtitle: row.Subtitle,
          __image: row.Image,
          __route: this.routeFor ? this.routeFor(row) : null,
          __shareRaw: row.Share || 0,
          __share: row.Share === null || row.Share === undefined ? '—' : `${this.round(row.Share, 1)}٪`,
          // مقدار خام برای مرتب‌سازی درست
          _cellVariants: {},
        }

        this.metrics.forEach(metric => {
          const raw = (row.Values && row.Values[metric.Key]) || 0
          item[metric.Key] = this.format(raw, metric.Format)
          item[`${metric.Key}__raw`] = raw

          const delta = row.Delta ? row.Delta[metric.Key] : null

          if (delta !== null && delta !== undefined) {
            const sign = delta > 0 ? '+' : ''
            item[`${metric.Key}__delta`] = `${sign}${this.round(delta, 1)}٪`
            item[`${metric.Key}__variant`] = delta > 0 ? 'success' : 'danger'
          }
        })

        return item
      })
    },
  },
  methods: {
    round(value, digits) {
      const factor = 10 ** digits
      return Math.round(value * factor) / factor
    },

    group(value) {
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    format(value, formatType) {
      if (value === null || value === undefined) return '—'
      if (formatType === 'percent') return `${this.round(value, 1)}٪`
      if (formatType === 'price') return `${this.group(Math.round(value))} تومان`
      if (formatType === 'duration') return `${this.group(Math.round(value))} ms`
      return this.group(this.round(value, 2))
    },

    initials(name) {
      if (!name) return ''
      return name.split(' ').slice(0, 2).map(w => w.charAt(0)).join('')
    },

    /**
     * تصاویر آپلودی هر سایت زیر /upload سرو می‌شوند (میدلور Eram آن را به
     * /{siteId} بازنویسی می‌کند). مقدار خالی یعنی آواتار متنی.
     */
    imageUrl(image) {
      if (!image) return null
      if (/^https?:\/\//.test(image)) return image
      return `${process.env.VUE_APP_BASE_URL ? process.env.VUE_APP_BASE_URL.replace(/api\/?$/, '') : '/'}upload/${image.replace(/^\/+/, '')}`
    },

    download() {
      const columns = [
        { label: this.labelTitle, value: row => row.__title },
        ...this.metrics.map(m => ({ label: m.Title, value: row => row[`${m.Key}__raw`] })),
      ]

      // BOM لازم است وگرنه اکسل ویندوز فارسی را خراب باز می‌کند
      const header = columns.map(c => this.csvCell(c.label)).join(',')
      const body = this.items.map(row => columns.map(c => this.csvCell(c.value(row))).join(',')).join('\n')
      const csv = `﻿${header}\n${body}`

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')

      link.href = URL.createObjectURL(blob)
      link.download = `${this.title}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },

    csvCell(value) {
      if (value === null || value === undefined) return ''
      const text = String(value)
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
    },
  },
}
</script>

<style lang="scss" scoped>
.report-table {
  height: 100%;

  &__subtitle {
    font-size: 0.75rem;
    color: #b9b9c3;
  }

  &__body {
    transition: opacity 0.15s ease;

    &--busy {
      opacity: 0.45;
      pointer-events: none;
    }
  }

  &__label {
    min-width: 0;
  }

  &__name {
    display: block;
    font-size: 0.82rem;
    max-width: 16rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sub {
    display: block;
    font-size: 0.7rem;
    color: #b9b9c3;
  }

  &__value {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__delta {
    font-size: 0.68rem;
    margin-right: 0.35rem;
    white-space: nowrap;
  }

  &__bar {
    width: 3.5rem;
    height: 5px;
    border-radius: 3px;
    background: rgba(115, 103, 240, 0.14);
    overflow: hidden;
    margin-left: 0.5rem;
    flex-shrink: 0;
  }

  &__bar-fill {
    height: 100%;
    border-radius: 3px;
    background: #2a78d6;
  }

  &__share-value {
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    color: #b9b9c3;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #b9b9c3;
    font-size: 0.85rem;
    padding: 2.5rem 0;
  }

  ::v-deep td {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
}
</style>
